<template>
  <div class="flex w-full h-screen overflow-hidden bg-slate-100">
    <div class="flex-1 flex flex-col w-full min-h-0 overflow-hidden">
      <Topbar />

      <div class="flex-1 min-h-0 p-4 md:p-5 lg:p-6 mt-[64px] flex flex-col overflow-hidden">
        <div class="mb-4 flex shrink-0 items-start justify-between gap-3">
          <div>
            <h2 class="text-2xl font-semibold tracking-tight text-slate-900 md:text-3xl">Склад</h2>
            <p class="mt-1 text-sm text-slate-500 md:text-base">Текущие остатки, штрихкоды и списание по складу</p>
          </div>
          <div class="flex flex-wrap items-center justify-end gap-3 text-sm font-medium text-slate-600">
            <div class="rounded-xl border border-emerald-200 bg-emerald-50 px-4 py-2">
              Общее количество <span class="ml-1 font-semibold text-emerald-700">{{ formattedTotalQuantity }}</span>
            </div>
            <div class="rounded-xl border border-sky-200 bg-sky-50 px-4 py-2">
              Общая сумма <span class="ml-1 font-semibold text-sky-700">{{ formattedTotal }} ₸</span>
            </div>
          </div>
        </div>

        <Toast/>
        <div class="min-h-0 flex-1 rounded-2xl border border-slate-200 bg-white p-4 shadow-sm md:p-6 overflow-hidden">
        <DataTable ref="storeTable" :value="filteredStore" :filters="filters" filterDisplay="menu" :globalFilterFields="['name', 'counterparty', 'date', 'barcode', 'description', 'amount', 'unit', 'classification', 'category', 'price', 'realization_price']" 
            class="store-table flex-1 min-h-0 h-full"
            scrollable scrollHeight="flex" resizableColumns columnResizeMode="fit" removableSort showGridlines stripedRows size="small"
            >
          <template #header>
            <div class="flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between pb-3">
              <div class="flex w-full flex-1 flex-col gap-3">
                <div class="flex w-full flex-wrap items-center gap-2">
                  <IconField class="w-full sm:w-auto">
                    <InputIcon>
                      <i class="pi pi-search" />
                    </InputIcon>
                    <InputText v-model="filters['global'].value" placeholder="Поиск по складу..." />
                  </IconField>
                  <FloatLabel variant="on">
                      <label for="storage" class="block z-10">Склад</label>
                      <Select id="storage" v-model="selectedStorage" :options="storages" optionLabel="name" optionValue="id" class="w-40" required></Select>
                  </FloatLabel>
                  <Select
                    v-model="selectedClassificationFilter"
                    :options="classificationOptions"
                    placeholder="Все классы"
                    showClear
                    class="w-full sm:w-52"
                  />
                  <Select
                    v-model="selectedCategoryFilter"
                    :options="filteredCategoryOptions"
                    placeholder="Все категории"
                    showClear
                    :disabled="!selectedClassificationFilter"
                    class="w-full sm:w-52"
                  />
                  <Button type="button" icon="pi pi-filter-slash" label="Очистить" variant="outlined" severity="danger" @click="clearFilter()" class="w-full sm:w-auto"/>
                  <div class="ml-auto flex flex-wrap items-center justify-end gap-1">
                    <Button @click="openImportDialog()" icon="pi pi-file-import" severity="contrast" text v-tooltip.top="{ value: 'Импорт Excel'}"/>
                    <Button @click="exportCSV()" icon="pi pi-upload" severity="secondary" text v-tooltip.top="{ value: 'Экспорт CSV'}"/>
                  </div>
                </div>
              </div>
            </div>

            <div class="flex flex-col gap-3 border-t border-slate-200 pt-3 xl:flex-row xl:items-center xl:justify-between">
              <div class="flex flex-wrap items-center gap-2">
                <Button
                  :label="`В наличии (${inStockCount})`"
                  icon="pi pi-check-circle"
                  size="small"
                  :severity="activeQuickFilter === 'in-stock' ? 'success' : 'secondary'"
                  :outlined="activeQuickFilter !== 'in-stock'"
                  :class="activeQuickFilter === 'in-stock' ? '!shadow-sm' : '!border-emerald-200 !bg-emerald-50 !text-emerald-800 hover:!bg-emerald-100'"
                  @click="toggleQuickFilter('in-stock')"
                />
                <Button
                  :label="`Нет в наличии (${outOfStockCount})`"
                  icon="pi pi-ban"
                  size="small"
                  severity="secondary"
                  :outlined="activeQuickFilter !== 'out-of-stock'"
                  :class="activeQuickFilter === 'out-of-stock' ? '!shadow-sm' : '!border-slate-300 !bg-slate-100 !text-slate-800 hover:!bg-slate-200'"
                  @click="toggleQuickFilter('out-of-stock')"
                />
                <Button
                  :label="`Истекает срок (${expiringSoonCount})`"
                  icon="pi pi-clock"
                  size="small"
                  :severity="activeQuickFilter === 'expiring' ? 'warn' : 'secondary'"
                  :outlined="activeQuickFilter !== 'expiring'"
                  :class="activeQuickFilter === 'expiring' ? '!shadow-sm' : '!border-orange-200 !bg-orange-50 !text-orange-800 hover:!bg-orange-100'"
                  @click="toggleQuickFilter('expiring')"
                />
                <Button
                  :label="`Малый остаток (${lowStockCount})`"
                  icon="pi pi-exclamation-circle"
                  size="small"
                  :severity="activeQuickFilter === 'low-stock' ? 'warn' : 'secondary'"
                  :outlined="activeQuickFilter !== 'low-stock'"
                  :class="activeQuickFilter === 'low-stock' ? '!shadow-sm' : '!border-amber-200 !bg-amber-50 !text-amber-800 hover:!bg-amber-100'"
                  @click="toggleQuickFilter('low-stock')"
                />
                <Button
                  :label="`Нет цены на реализацию (${missingRealizationPriceCount})`"
                  icon="pi pi-tag"
                  size="small"
                  :severity="activeQuickFilter === 'missing-realization-price' ? 'danger' : 'secondary'"
                  :outlined="activeQuickFilter !== 'missing-realization-price'"
                  :class="activeQuickFilter === 'missing-realization-price' ? '!shadow-sm' : '!border-rose-200 !bg-rose-50 !text-rose-800 hover:!bg-rose-100'"
                  @click="toggleQuickFilter('missing-realization-price')"
                />
              </div>

              <div class="flex flex-wrap items-center justify-start gap-2 xl:justify-end">
                <Button
                  label="Списанные товары"
                  icon="pi pi-eraser"
                  size="small"
                  severity="warn"
                  outlined
                  class="!border-amber-200 !bg-amber-50 !text-amber-800 hover:!bg-amber-100"
                  @click="goToWriteOffProducts()"
                />
                <Button
                  label="Тариф за сбор заказа"
                  icon="pi pi-tag"
                  size="small"
                  severity="info"
                  outlined
                  class="!border-sky-200 !bg-sky-50 !text-sky-800 hover:!bg-sky-100"
                  @click="goToOrderTypePrice()"
                />
                <Button
                  label="Цена реализации"
                  icon="pi pi-dollar"
                  size="small"
                  severity="success"
                  outlined
                  class="!border-emerald-200 !bg-emerald-50 !text-emerald-800 hover:!bg-emerald-100"
                  @click="goToStorePriceByDay()"
                />
              </div>
            </div>
          </template>
          <template #empty> Не найдено. </template>
          <Column field="name" header="Название" frozen badge="2">
            <template #filter="{ filterModel }">
                <InputText v-model="filterModel.value" type="text" placeholder="Search by name" />
            </template>
          </Column>
          <Column field="image" header="Фото" class="!text-center">
            <template #body="{ data }">
              <Image :src="data.image" :alt="data.image" class="shadow-lg cursor-pointer" width="64" preview v-if="data.image"/>
            </template>
          </Column>
          <Column field="classification" header="Класс"> </Column>
          <Column field="category"       header="Категория" ></Column>
          <Column field="date" sortable header="Срок годности">
            <template #body="{ data }">
              <div class="flex flex-col">
                <span>{{ data.date || '—' }}</span>
                <span
                  v-if="isExpiringSoon(data)"
                  class="text-xs font-medium"
                  :class="getDaysUntilExpiry(data.date) < 0 ? 'text-rose-700' : 'text-orange-700'"
                >
                  {{ getDaysUntilExpiry(data.date) < 0 ? `Просрочен на ${Math.abs(getDaysUntilExpiry(data.date))} дн.` : `Осталось ${getDaysUntilExpiry(data.date)} дн.` }}
                </span>
              </div>
            </template>
          </Column>
          <Column field="barcode"        sortable header="Штрихкод"></Column>
          <Column field="unit"           sortable header="Ед. изм."></Column>
          <Column field="amount" sortable header="Количество">
            <template #body="{ data }">
              <span
                :class="{
                  'font-semibold text-slate-700': !isOutOfStock(data) && !isLowStock(data),
                  'font-semibold text-amber-700': isLowStock(data),
                  'font-semibold text-slate-500': isOutOfStock(data),
                }"
              >
                {{ data.amount }}
              </span>
            </template>
          </Column>
          <Column field="price" sortable header="Закуп. цена">
            <template #body="{ data }">
              <span>{{ data.price }}</span>
            </template>
          </Column>
          <Column field="realization_price" sortable header="Цена реализации">
            <template #body="{ data }">
              <div class="flex flex-col">
                <span>{{ data.realization_price ?? '—' }}</span>
                <span v-if="isMissingRealizationPrice(data)" class="text-xs font-medium text-rose-700">
                  Нет цены реализации
                </span>
              </div>
            </template>
          </Column>
          <Column field="net"            sortable header="Себес" ></Column>
          <Column :exportable="false" header="Действие" class="!text-center">
            <template #body="{ data }">
              <div class="flex items-center justify-center gap-2">
                <Button icon="pi pi-pencil" severity="secondary" outlined @click="openEditAmountDialog(data)"></Button>
                <Button icon="pi pi-trash" severity="danger" outlined @click="openItemDialog(data)" :disabled="data.amount == 0"></Button>
              </div>
            </template>
          </Column>
          <ColumnGroup type="footer" v-if="store">
            <Row>
                <Column footer="Итого:" footerStyle="text-align:right" frozen/>
                <Column :colspan="9" footerStyle="text-align:right" />
                <Column :footer="formattedTotal" footerStyle="text-align:right" />
                <Column footerStyle="text-align:right" />
            </Row>
          </ColumnGroup>
        </DataTable>
        <Dialog v-model:visible="removeItemDialog" :style="{ width: '600px' }" header="Списать товар?" :modal="true">
            <div class="flex justify-center items-center gap-4">
              <div class="w-full">
                <div class="grid grid-cols-12 justify-between gap-4 mb-6">
                  <div class="flex items-center gap-2 col-span-12">
                    <FloatLabel variant="on" class="col-span-3 mt-2">
                      <label for="name" class="block z-10"> Название </label>
                      <InputText id="name" :min="1000" v-model.trim="selectedItem.name" fluid disabled/>
                    </FloatLabel>
                    <FloatLabel variant="on" class="col-span-3 mt-2">
                      <label for="barcode" class="block z-10"> Штрихкод </label>
                      <InputNumber id="barcode" :min="1000" v-model.trim="selectedItem.barcode" fluid disabled/>
                    </FloatLabel>
                    <FloatLabel variant="on" class="col-span-3 mt-2">
                      <label for="classification" class="block z-10"> Класс </label>
                      <InputText id="name" :min="1000" v-model.trim="selectedItem.classification" fluid disabled/>
                    </FloatLabel>
                    <FloatLabel variant="on" class="col-span-3 mt-2">
                      <label for="category" class="block z-10"> Категория </label>
                      <InputText id="name" :min="1000" v-model.trim="selectedItem.category" fluid disabled/>
                    </FloatLabel>
                  </div>
                  <div class="flex items-center gap-2 col-span-12">
                    <FloatLabel variant="on" class="col-span-3 mt-2">
                      <label for="counterparty" class="block z-10"> Контрагент </label>
                      <InputText id="counterparty" :min="1000" v-model.trim="selectedItem.counterparty" fluid disabled/>
                    </FloatLabel>
                    <FloatLabel variant="on" class="col-span-3 mt-2">
                      <label for="amount" class="block z-10"> Количество </label>
                      <InputNumber id="amount" :min="1000" v-model.trim="selectedItem.amount" fluid disabled/>
                    </FloatLabel>
                    <FloatLabel variant="on" class="col-span-3 mt-2">
                      <label for="unit" class="block z-10"> Ед. изм. </label>
                      <InputText id="counterparty" :min="1000" v-model.trim="selectedItem.unit" fluid disabled/>
                    </FloatLabel>
                    <FloatLabel variant="on" class="col-span-3 mt-2">
                      <label for="remove" class="block z-10"> Cписать ед.</label>
                      <InputNumber id="remove" :min="1" :max="selectedItem.amount" v-model.trim="selectedItem.quantity" fluid required/>
                    </FloatLabel>
                  </div>
                  <div class="flex items-center gap-2 col-span-12">
                    <div class="flex flex-col items-center w-1/2">
                      <FileUpload 
                        class="border-2 border-amber-200"
                        severity="info"
                        mode="basic" 
                        accept="image/*"
                        auto
                        fluid
                        customUpload
                        chooseLabel="Добавить фото"
                        :chooseButtonProps="{ severity: 'secondary' }" 
                        @select="onSelect"
                      />
                      <img v-if="preview" :src="preview" width="150" class="mt-2" />
                    </div>
                    <div class="flex flex-col justify-start items-start w-1/2 h-full">
                      <FloatLabel variant="on" class="w-full h-full">
                        <label class="block z-10"> Причина </label>
                        <Textarea v-model="selectedItem.reason" fluid required class="h-full"/>
                      </FloatLabel>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <template #footer >
              <div class="flex justify-evenly w-full gap-2">
                <Button label="Отменить" icon="pi pi-times" @click="removeItemDialog = false" severity="danger" fluid/>
                <Button label="Списать" icon="pi pi-check" @click="removeItem()" severity="success" fluid/>
              </div>
            </template>
          </Dialog>
          <Dialog v-model:visible="editAmountDialog" :style="{ width: '520px' }" header="Изменить количество?" :modal="true">
            <div class="flex flex-col gap-4">
              <div class="grid grid-cols-1 gap-3 md:grid-cols-2">
                <FloatLabel variant="on">
                  <label for="edit-name" class="block z-10">Название</label>
                  <InputText id="edit-name" v-model.trim="editableItem.name" fluid disabled />
                </FloatLabel>
                <FloatLabel variant="on">
                  <label for="edit-barcode" class="block z-10">Штрихкод</label>
                  <InputNumber id="edit-barcode" v-model="editableItem.barcode" fluid disabled />
                </FloatLabel>
                <FloatLabel variant="on">
                  <label for="edit-unit" class="block z-10">Ед. изм.</label>
                  <InputText id="edit-unit" v-model.trim="editableItem.unit" fluid disabled />
                </FloatLabel>
                <FloatLabel variant="on">
                  <label for="edit-current-amount" class="block z-10">Текущее кол-во</label>
                  <InputNumber id="edit-current-amount" v-model="editableItem.currentAmount" fluid disabled />
                </FloatLabel>
              </div>

              <FloatLabel variant="on">
                <label for="edit-new-amount" class="block z-10">Новое кол-во</label>
                <InputNumber id="edit-new-amount" v-model="editableItem.amount" :min="0" fluid required />
              </FloatLabel>
            </div>
            <template #footer>
              <div class="flex justify-evenly w-full gap-2">
                <Button label="Отменить" icon="pi pi-times" @click="closeEditAmountDialog()" severity="danger" fluid />
                <Button label="Сохранить" icon="pi pi-check" @click="updateItemAmount()" severity="success" fluid />
              </div>
            </template>
          </Dialog>
          <Dialog v-model:visible="imageDialog" :style="{ width: '500px' }" :header="selectedProduct.name" :modal="true" v-if="selectedProduct">
            <div class="flex justify-center items-center gap-4">
              <div class="w-full">
                <img :src="selectedProduct.image" :alt="selectedProduct.name" class="w-full h-full object-cover"/>
              </div>
            </div>
          </Dialog>
          <Dialog v-model:visible="importDialog" :style="{ width: '560px' }" header="Импорт склада из Excel" :modal="true">
            <div class="flex flex-col gap-4">
              <div class="text-sm text-gray-600">
                Поддерживается файл с колонками: <b>Контрагент</b>, <b>Штрих код</b>, <b>Срок</b>, <b>Ед. Изм</b>, <b>Кол-во</b>, <b>Цена</b>, <b>Себестоимость</b>.
              </div>
              <div class="flex flex-col gap-2">
                <FileUpload
                  mode="basic"
                  accept=".xlsx,.xls,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/vnd.ms-excel"
                  customUpload
                  chooseLabel="Выбрать Excel"
                  :chooseButtonProps="{ severity: 'secondary' }"
                  @select="onImportFileSelect"
                />
                <div v-if="importFile" class="text-sm text-gray-600">
                  Файл: {{ importFile.name }}
                </div>
              </div>
            </div>
            <template #footer>
              <div class="flex justify-evenly w-full gap-2">
                <Button label="Отменить" icon="pi pi-times" @click="closeImportDialog()" severity="danger" fluid />
                <Button :label="importLoading ? 'Импорт...' : 'Загрузить'" :loading="importLoading" icon="pi pi-check" @click="importStoreFromExcel()" severity="success" fluid />
              </div>
            </template>
          </Dialog>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted, computed, watch } from 'vue'
import axios from '../lib/axios'
import * as XLSX from 'xlsx'
import { FilterMatchMode, FilterOperator } from '@primevue/core/api';
import { useRouter } from 'vue-router'
import { useToast } from 'primevue/usetoast'
import Toast from 'primevue/toast'

const toast = useToast()

const router = useRouter()
const store = ref([])
const filters = ref([])
const storages = ref([])
const selectedStorage = ref(0)
const selectedClassificationFilter = ref(null)
const selectedCategoryFilter = ref(null)
const activeQuickFilter = ref('all')
const storeTable = ref()
const removeItemDialog = ref(false)
const selectedItem = ref([])
const editAmountDialog = ref(false)
const editableItem = ref({})
const selectedFile = ref(null);
const preview = ref(null);
const imageDialog = ref([])
const selectedProduct = ref()
const importDialog = ref(false)
const importFile = ref(null)
const importLoading = ref(false)
const LOW_STOCK_THRESHOLD = 5
const EXPIRING_SOON_MONTHS = 3

watch(selectedStorage, (newValue, oldValue) => {
  fetchStore()
})

watch(selectedClassificationFilter, (nextValue, previousValue) => {
  if (nextValue !== previousValue) {
    selectedCategoryFilter.value = null
  }
})

const openImage = (product) => {
  if (product) {
    imageDialog.value = true
    selectedProduct.value = product
  }
}

const openImportDialog = () => {
  importDialog.value = true
}

const closeImportDialog = () => {
  importDialog.value = false
  importFile.value = null
  importLoading.value = false
}

const onImportFileSelect = (e) => {
  importFile.value = e.files?.[0] ?? null
}

const normalizeCellValue = (value) => String(value ?? '').trim()

const normalizeHeader = (value) => normalizeCellValue(value).toLowerCase().replace(/\s+/g, ' ')

const normalizeNumber = (value) => {
  const sanitized = normalizeCellValue(value).replace(/\s+/g, '').replace(',', '.')
  return sanitized === '' ? NaN : Number(sanitized)
}

const readExcelRows = async (file) => {
  const buffer = await file.arrayBuffer()
  const workbook = XLSX.read(buffer, { type: 'array' })
  const firstSheetName = workbook.SheetNames[0]

  if (!firstSheetName) {
    throw new Error('Файл Excel не содержит листов')
  }

  return XLSX.utils.sheet_to_json(workbook.Sheets[firstSheetName], {
    header: 1,
    defval: '',
    raw: false,
  })
}

const getImportColumnIndexes = (headers) => {
  const normalizedHeaders = headers.map(normalizeHeader)
  const findColumn = (variants) => normalizedHeaders.findIndex(header => variants.includes(header))

  const indexes = {
    counterparty: findColumn(['контрагент']),
    barcode: findColumn(['штрих код', 'штрихкод']),
    date: findColumn(['срок']),
    unit: findColumn(['ед. изм', 'ед. изм.', 'ед изм', 'ед изм.']),
    amount: findColumn(['кол-во', 'количество']),
    price: findColumn(['цена']),
    net: findColumn(['себестоимость']),
  }

  if (Object.values(indexes).some(index => index === -1)) {
    throw new Error('В Excel не найдены обязательные колонки для склада')
  }

  return indexes
}

const buildStoreImportRows = (rows) => {
  if (!rows.length) {
    throw new Error('Файл Excel пустой')
  }

  const indexes = getImportColumnIndexes(rows[0])
  const preparedRows = []

  rows.slice(1).forEach((row) => {
    const counterparty = normalizeCellValue(row[indexes.counterparty])
    const barcode = normalizeCellValue(row[indexes.barcode])
    const date = normalizeCellValue(row[indexes.date])
    const unit = normalizeCellValue(row[indexes.unit])
    const amount = normalizeNumber(row[indexes.amount])
    const price = normalizeNumber(row[indexes.price])
    const net = normalizeNumber(row[indexes.net])

    const isEmptyRow = !counterparty && !barcode && !date && !unit && Number.isNaN(amount) && Number.isNaN(price) && Number.isNaN(net)
    if (isEmptyRow) {
      return
    }

    preparedRows.push({
      counterparty,
      barcode,
      date,
      unit,
      amount,
      price,
      net,
    })
  })

  if (!preparedRows.length) {
    throw new Error('В Excel нет строк для импорта')
  }

  return preparedRows
}

const importStoreFromExcel = async () => {
  if (!importFile.value) {
    toast.add({ severity: 'error', summary: 'Ошибка', detail: 'Выберите Excel-файл', life: 5000 })
    return
  }

  importLoading.value = true

  try {
    const rows = await readExcelRows(importFile.value)
    const payloadRows = buildStoreImportRows(rows)
    const { data } = await axios.post('/api/store/import', { rows: payloadRows })

    await fetchStore()

    const problemsCount = Array.isArray(data?.errors) ? data.errors.length : 0
    const detail = problemsCount > 0
      ? `Импортировано: ${data.imported}. Ошибок/пропусков: ${problemsCount}`
      : `Импортировано: ${data.imported}`

    toast.add({
      severity: problemsCount > 0 ? 'warn' : 'success',
      summary: problemsCount > 0 ? 'Импорт завершён частично' : 'Успешно!',
      detail,
      life: 8000,
    })

    if (problemsCount > 0) {
      console.table(data.errors.map(message => ({ message })))
    }

    closeImportDialog()
  } catch (error) {
    const backendErrors = error?.response?.data?.errors
    const detail = Array.isArray(backendErrors)
      ? backendErrors[0]
      : error?.response?.data?.message || error?.message || 'Не удалось импортировать склад'

    toast.add({ severity: 'error', summary: 'Ошибка', detail, life: 7000 })
    console.error(error)
  } finally {
    importLoading.value = false
  }
}

const initFilters = () => {
    filters.value = {
        global: { value: null, matchMode: FilterMatchMode.CONTAINS },
        name: { operator: FilterOperator.AND, constraints: [{ value: null, matchMode: FilterMatchMode.STARTS_WITH }] },
        // counterparty: { operator: FilterOperator.AND, constraints: [{ value: null, matchMode: FilterMatchMode.STARTS_WITH }] },
        // date: { operator: FilterOperator.AND, constraints: [{ value: null, matchMode: FilterMatchMode.DATE_IS }] },
        // barcode: { value: null, matchMode: FilterMatchMode.IN },
        // classification: { operator: FilterOperator.AND, constraints: [{ value: null, matchMode: FilterMatchMode.EQUALS }] },
        // category: { operator: FilterOperator.OR, constraints: [{ value: null, matchMode: FilterMatchMode.EQUALS }] },
        // description: { value: [0, 100], matchMode: FilterMatchMode.BETWEEN },
        // unit: { value: null, matchMode: FilterMatchMode.EQUALS },
        // amount: { value: null, matchMode: FilterMatchMode.EQUALS },
        // price: { value: null, matchMode: FilterMatchMode.EQUALS },
        // net: { value: null, matchMode: FilterMatchMode.EQUALS }
    };
};

initFilters();

const clearFilter = () => {
  initFilters()
  selectedClassificationFilter.value = null
  selectedCategoryFilter.value = null
  activeQuickFilter.value = 'all'
}

const classificationOptions = computed(() => {
  return [...new Set(
    store.value
      .map((item) => String(item.classification ?? '').trim())
      .filter(Boolean)
  )].sort((a, b) => a.localeCompare(b, 'ru'))
})

const filteredCategoryOptions = computed(() => {
  const items = store.value.filter((item) => {
    if (!selectedClassificationFilter.value) {
      return true
    }

    return String(item.classification ?? '').trim() === selectedClassificationFilter.value
  })

  return [...new Set(
    items
      .map((item) => String(item.category ?? '').trim())
      .filter(Boolean)
  )].sort((a, b) => a.localeCompare(b, 'ru'))
})

const baseFilteredStore = computed(() => {
  return store.value.filter((item) => {
    const classificationMatch =
      !selectedClassificationFilter.value ||
      String(item.classification ?? '').trim() === selectedClassificationFilter.value

    const categoryMatch =
      !selectedCategoryFilter.value ||
      String(item.category ?? '').trim() === selectedCategoryFilter.value

    return classificationMatch && categoryMatch
  })
})

const outOfStockCount = computed(() => {
  return baseFilteredStore.value.filter((item) => isOutOfStock(item)).length
})

const inStockCount = computed(() => {
  return baseFilteredStore.value.filter((item) => isInStock(item)).length
})

const expiringSoonCount = computed(() => {
  return baseFilteredStore.value.filter((item) => isExpiringSoon(item)).length
})

const lowStockCount = computed(() => {
  return baseFilteredStore.value.filter((item) => isLowStock(item)).length
})

const missingRealizationPriceCount = computed(() => {
  return baseFilteredStore.value.filter((item) => isMissingRealizationPrice(item)).length
})

const filteredStore = computed(() => {
  return baseFilteredStore.value.filter((item) => {
    if (activeQuickFilter.value === 'in-stock') {
      return isInStock(item)
    }

    if (activeQuickFilter.value === 'out-of-stock') {
      return isOutOfStock(item)
    }

    if (activeQuickFilter.value === 'expiring') {
      return isExpiringSoon(item)
    }

    if (activeQuickFilter.value === 'low-stock') {
      return isLowStock(item)
    }

    if (activeQuickFilter.value === 'missing-realization-price') {
      return isMissingRealizationPrice(item)
    }

    return true
  })
})

const toggleQuickFilter = (filterName) => {
  activeQuickFilter.value = activeQuickFilter.value === filterName ? 'all' : filterName
}

const getDaysUntilExpiry = (value) => {
  if (!value) return null

  const expiryDate = new Date(value)
  if (Number.isNaN(expiryDate.getTime())) return null

  expiryDate.setHours(0, 0, 0, 0)

  const today = new Date()
  today.setHours(0, 0, 0, 0)

  return Math.ceil((expiryDate.getTime() - today.getTime()) / 86400000)
}

const isOutOfStock = (item) => {
  return Number(item?.amount || 0) <= 0
}

const isInStock = (item) => {
  return Number(item?.amount || 0) > 0
}

const isLowStock = (item) => {
  const amount = Number(item?.amount || 0)
  return amount > 0 && amount <= LOW_STOCK_THRESHOLD
}

const isExpiringSoon = (item) => {
  const daysLeft = getDaysUntilExpiry(item?.date)
  if (Number(item?.amount || 0) <= 0 || daysLeft === null) return false

  const today = new Date()
  today.setHours(0, 0, 0, 0)

  const warningDate = new Date(today)
  warningDate.setMonth(warningDate.getMonth() + EXPIRING_SOON_MONTHS)
  warningDate.setHours(0, 0, 0, 0)

  const expiryDate = new Date(item?.date)
  if (Number.isNaN(expiryDate.getTime())) return false
  expiryDate.setHours(0, 0, 0, 0)

  return expiryDate <= warningDate
}

const isMissingRealizationPrice = (item) => {
  return !item?.has_realization_price
}

const onSelect = (e) => {
  selectedFile.value = e.files[0];
  preview.value = URL.createObjectURL(selectedFile.value);
};

const formatDate = (value) => {
    return value.toLocaleDateString('en-US', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric'
    });
};

const openItemDialog = (item) => {
  selectedItem.value = {
    ...item,
    store_id: item.store_id ?? item.id,
  };
  removeItemDialog.value = true;
}

const openEditAmountDialog = (item) => {
  editableItem.value = {
    ...item,
    store_id: item.store_id ?? item.id,
    currentAmount: Number(item.amount || 0),
    amount: Number(item.amount || 0),
  }
  editAmountDialog.value = true
}

const closeEditAmountDialog = () => {
  editableItem.value = {}
  editAmountDialog.value = false
}

const removeItem = async () => {
  if (!selectedItem.value.quantity || selectedItem.value.quantity == 0) {
    toast.add({ severity: 'error', summary: 'Ошибка', detail: 'Количество должно быть больше 0', life: 3000 });
  }   else if (!selectedItem.value.reason){
    toast.add({ severity: 'error', summary: 'Ошибка', detail: 'Укажите причину', life: 3000 });
  }   else {
    selectedItem.value.image = selectedFile.value;
    await axios.post(`/api/store/${selectedItem.value.store_id}?_method=PUT`, selectedItem.value, {
      headers: { "Content-Type": "multipart/form-data"}
    });

    toast.add({ severity: 'success', summary: 'Успех', message: 'Товар списан!', life: 3000 })
    removeItemDialog.value = false;
    fetchStore();
  }
}

const updateItemAmount = async () => {
  const amount = Number(editableItem.value.amount)

  if (!Number.isFinite(amount) || amount < 0) {
    toast.add({ severity: 'error', summary: 'Ошибка', detail: 'Количество должно быть больше или равно 0', life: 3000 })
    return
  }

  try {
    await axios.put(`/api/store/${editableItem.value.store_id}/amount`, { amount })
    toast.add({ severity: 'success', summary: 'Успех', detail: 'Количество обновлено', life: 3000 })
    closeEditAmountDialog()
    await fetchStore()
  } catch (error) {
    const detail = error?.response?.data?.message || 'Не удалось обновить количество'
    toast.add({ severity: 'error', summary: 'Ошибка', detail, life: 4000 })
  }
}

const formatCurrency = (value) => {
    return value.toLocaleString('en-US', { style: 'currency', currency: 'USD' });
};

const fetchStore = async () => {
  const res = await axios.get('/api/store', {
    params: {
      storage: selectedStorage.value,
      price_source: 'base',
    },
  })
  store.value = Array.isArray(res.data) ? res.data : Object.values(res.data)
}
const getStorages = async () => {
  const res = await axios.get('/api/get-storage') 
  storages.value = res.data;
  storages.value.unshift({id: 0, name: 'Все'});
}
const exportCSV = () => {
  storeTable.value.exportCSV({ selectionOnly: false});
}
const goToOrderTypePrice = () => {
  router.push({name: 'OrderTypePrice'})
}
const goToWriteOffProducts = () => {
  router.push({name: 'WriteOffProducts'})
}
const goToStorePriceByDay = () => {
  router.push({name: 'StorePriceByDay'})
}

const summaryStore = computed(() => filteredStore.value)

const formattedTotal = computed(() => {
  const sum = summaryStore.value.reduce((acc, b) => acc + (Number(b.net) || 0), 0)
  return new Intl.NumberFormat('ru-RU', { style: 'decimal', minimumFractionDigits: 0  }).format(sum)
})

const formattedTotalQuantity = computed(() => {
  const totalQuantity = summaryStore.value.reduce((acc, item) => {
    const category = String(item.category ?? '').trim()
    const quantity = category === 'Конфеты на развес' ? 1 : (Number(item.amount) || 0)
    return acc + quantity
  }, 0)
  return new Intl.NumberFormat('ru-RU', { style: 'decimal', minimumFractionDigits: 0 }).format(totalQuantity)
})

onMounted(() => {
  fetchStore()
  getStorages()
})
</script>

<style scoped>
:deep(.store-table),
:deep(.store-table .p-datatable-table-container) {
  height: 100%;
}

:deep(.store-table .p-datatable-header) {
  border: 0;
  border-bottom: 1px solid rgb(226 232 240);
  background: rgb(255 255 255);
  padding: 0 0 1rem 0;
}

:deep(.store-table .p-datatable-thead > tr > th) {
  background: rgb(248 250 252);
  color: rgb(71 85 105);
  font-weight: 600;
  border-color: rgb(226 232 240);
  padding: 0.875rem 1rem;
}

:deep(.store-table .p-datatable-tbody > tr > td) {
  border-color: rgb(241 245 249);
  color: rgb(51 65 85);
  padding: 0.875rem 1rem;
  transition: background-color 150ms ease;
}

:deep(.store-table .p-datatable-tbody > tr:hover > td) {
  background: rgba(236, 253, 245, 0.55);
}

:deep(.store-table .p-datatable-tbody > tr > td.p-frozen-column),
:deep(.store-table .p-datatable-thead > tr > th.p-frozen-column),
:deep(.store-table .p-datatable-tfoot > tr > td.p-frozen-column) {
  background: inherit;
}

:deep(.store-table .p-datatable-tfoot > tr > td) {
  background: rgb(255 255 255);
  border-color: rgb(226 232 240);
  font-weight: 600;
  color: rgb(30 41 59);
  padding: 0.875rem 1rem;
}

:deep(.store-table .p-paginator) {
  border-top: 1px solid rgb(226 232 240);
  background: rgb(255 255 255);
  padding: 0.75rem 1rem;
}

:deep(.store-table .p-paginator-page),
:deep(.store-table .p-paginator-next),
:deep(.store-table .p-paginator-prev),
:deep(.store-table .p-paginator-first),
:deep(.store-table .p-paginator-last) {
  min-width: 2rem;
  height: 2rem;
  border-radius: 0.625rem;
}

:deep(.store-table .p-paginator-page.p-paginator-page-selected) {
  background: rgb(16 185 129);
  color: rgb(255 255 255);
}

:deep(.store-table .p-column-header-content) {
  gap: 0.375rem;
}

:deep(.store-table .p-sortable-column:not(.p-datatable-column-sorted):hover) {
  background: rgb(241 245 249);
}
</style>
